import React from 'react';
import { Button, Icon, Text, VStack } from '@chakra-ui/react';
import { FiFacebook } from 'react-icons/fi';
import useAppToast from 'components/toast';
import { useStartEmbeddedSignupMutation } from 'api/metaWhatsappApi';
import { explainRefusal } from './_shared';

/**
 * "Connect with Facebook" — Meta's Embedded Signup on the platform's app.
 *
 * ★ THE BROWSER TALKS TO THE CONNECTION LAYER DIRECTLY FOR THE CALLBACK.
 * The CRM mints the session (it knows who the tenant is); Meta's dialog hands
 * the browser a one-time code plus the account and number ids; the browser
 * posts those straight to the connection layer, the only service holding the
 * Meta app secret. The CRM never sees the code. The `sessionId` + `state` pair
 * is the whole authentication of that call, which is why it is single-use.
 *
 * The SDK is loaded on demand, once: nothing from facebook.com runs on pages
 * that never connect anything.
 */

const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

const loadSdk = () =>
	new Promise((resolve, reject) => {
		if (window.FB) return resolve(window.FB);
		const existing = document.getElementById('facebook-jssdk');
		if (existing) {
			existing.addEventListener('load', () => resolve(window.FB));
			existing.addEventListener('error', () => reject(new Error('SDK_LOAD_FAILED')));
			return undefined;
		}
		const script = document.createElement('script');
		script.id = 'facebook-jssdk';
		script.src = SDK_SRC;
		script.async = true;
		script.onload = () => resolve(window.FB);
		script.onerror = () => reject(new Error('SDK_LOAD_FAILED'));
		document.body.appendChild(script);
		return undefined;
	});

export default function EmbeddedSignupButton({ signup, onConnected, size = 'md' }) {
	const [startSession] = useStartEmbeddedSignupMutation();
	const notify = useAppToast();
	const [busy, setBusy] = React.useState(false);

	const available = Boolean(signup?.available && signup?.exchangeUrl);

	const run = async () => {
		setBusy(true);
		let session;
		try {
			session = await startSession().unwrap();
			await loadSdk();
		} catch (err) {
			setBusy(false);
			notify.error('Not connected', err?.message === 'SDK_LOAD_FAILED' ? 'The Facebook SDK could not be loaded. Check the browser is allowed to reach facebook.com.' : explainRefusal(err?.data ?? {}));
			return;
		}

		window.FB.init({ appId: session.metaAppId, autoLogAppEvents: false, xfbml: false, version: session.graphVersion });

		// Meta posts the chosen account and number to the opener as a message,
		// separately from the login callback; both are needed for the exchange.
		const chosen = {};
		const onMessage = (event) => {
			if (!/facebook\.com$/.test(String(event.origin))) return;
			try {
				const data = JSON.parse(event.data);
				if (data?.type === 'WA_EMBEDDED_SIGNUP' && data.event === 'FINISH') {
					chosen.wabaId = data.data?.waba_id;
					chosen.phoneNumberId = data.data?.phone_number_id;
				}
			} catch (_) {
				// Not ours.
			}
		};
		window.addEventListener('message', onMessage);

		window.FB.login(
			async (response) => {
				window.removeEventListener('message', onMessage);
				const code = response?.authResponse?.code;
				if (!code) {
					setBusy(false);
					notify.warning('Not connected', 'The Facebook dialog was closed before it finished.');
					return;
				}
				try {
					const res = await fetch(session.exchangeUrl, {
						method: 'POST',
						headers: { 'content-type': 'application/json' },
						body: JSON.stringify({
							sessionId: session.sessionId,
							state: session.state,
							code,
							wabaId: chosen.wabaId,
							phoneNumberId: chosen.phoneNumberId,
						}),
					});
					const body = await res.json().catch(() => ({}));
					if (!res.ok && res.status !== 202) {
						throw Object.assign(new Error(body.error ?? 'EXCHANGE_FAILED'), { data: body });
					}
					notify.success('WhatsApp connected', `${(body.channels ?? []).length} number(s) are ready.`);
					onConnected?.(body);
				} catch (err) {
					notify.error('Not connected', explainRefusal(err?.data ?? {}));
				} finally {
					setBusy(false);
				}
			},
			{
				config_id: session.configId,
				response_type: 'code',
				override_default_response_type: true,
				extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
			},
		);
	};

	return (
		<VStack align="stretch" spacing="2">
			<Button
				leftIcon={<Icon as={FiFacebook} />}
				colorScheme="blue"
				size={size}
				onClick={run}
				isLoading={busy}
				isDisabled={!available}
			>
				Connect with Facebook
			</Button>
			{!available && (
				<Text fontSize="xs" color="text.muted">
					Not available on this installation: there is no platform Meta app configured. Use your own Meta
					app below.
				</Text>
			)}
		</VStack>
	);
}
